import { createContext, useContext, useEffect, useRef, useState } from "react";
import axiosInstance from "../libs/axiosInstance";
import { toast } from "sonner";
import { useNavigate } from "react-router";
import { uploadProfilePicture, verifyOtp, resendOtp, removeProfilePicture } from "../services/userService";
import { googleLogin } from "../services/authService";
import PageLoader from "../components/PageLoader";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [isLoading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("token") || null);
  const loggingOutRef = useRef(false);

  useEffect(() => {
    const fetchUser = async () => {
      if (token) {
        try {
          // Fetch user data from API
          const response = await axiosInstance.get("/account/profile/");
          if (response.data?.user) {
            setUser(response.data?.user);
            setProfile(response.data?.profile);
          }
        } catch (error) {
          if (error.response && error.response.status === 401) {
            console.error("Unauthorized access, logging out:", error);
            logout(); // Log out the user if it's a 401 error
          } else {
            console.error("Failed to fetch user data:", error);
          }
        }
      }
      setLoading(false); // Set loading to false after attempt to fetch
    };

    fetchUser();
  }, [token]);

  const updateProfile = async (data) => {
    try {
      const response = await axiosInstance.patch("/account/profile/", data);
      console.log("udpated profile: ", response.data);
      setUser(response.data.user);
      setProfile(response.data?.profile);
      return response.data?.user;
    } catch (error) {
      console.error("Updating profile failed:", error);
      throw error.response?.data || error.message;
    }
  };

  const updateProfilePic = async (file) => {
    try {
      const response = await uploadProfilePicture(file)
      console.log("udpated profile pic: ", response);
      console.log(response)
      setUser({...user, profile_pic: response.url});
      return response.url
    } catch (error) {
      console.error("Updating profile pic failed:", error);
      throw error.response?.data || error.message;
    }
  };

  const removeProfilePic = async () => {
    try {
      await removeProfilePicture();
      console.log("removed profile pic");
      setUser({...user, profile_pic: null});
      return null;
    } catch (error) {
      console.error("Removing profile pic failed:", error);
      throw error.response?.data || error.message;
    }
  };

  const register = async (data) => {
    try {
      const response = await axiosInstance.post("/account/create-account/", data);
      console.log("register response: ", response.data);
      setUser(response.data.user);
      setToken(response.data?.token);
      localStorage.setItem("token", response.data.token);
      return response.data?.user;
    } catch (error) {
      console.error("Registration failed:", error);
      throw error.response?.data || error.message;
    }
  };

  const login = async (data) => {
    try {
      const response = await axiosInstance.post("/account/login/", {
        email: data.email,
        password: data.password,
      });

      // Case B: Direct login when OTP is disabled
      if (response.data?.otp_required === false) {
        setUser(response.data.user);
        setProfile(response.data.profile || null);
        setToken(response.data.token);
        localStorage.setItem("token", response.data.token);
        return {
          otp_required: false,
          token: response.data.token,
          user: response.data.user,
          message: response.data.message || "Login successful",
        };
      }

      // Case A: OTP is required
      return {
        otp_required: true,
        email: response.data?.email || data.email,
        message: response.data?.message || "OTP sent to your email. It expires in 10 minutes.",
      };
    } catch (error) {
      console.error("Login failed:", error);
      let errorMessage =
        error.response?.data?.non_field_errors?.[0] ||
        error.response?.data?.detail ||
        error.response?.data?.error ||
        error?.message;
      if (errorMessage == "Invalid token.") {
        logout();
      }
      throw errorMessage;
    }
  };

  const verifyOtpLogin = async (payload) => {
    try {
      const response = await verifyOtp(payload);
      setUser(response?.user);
      setProfile(response?.profile);
      setToken(response?.token);
      localStorage.setItem("token", response.token);
      return response?.user;
    } catch (error) {
      console.error("OTP verification failed:", error);
      let errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.error ||
        error?.message;
      throw errorMessage;
    }
  };

  const resendOtpCode = async (payload) => {
    try {
      const response = await resendOtp(payload.email);
      return response;
    } catch (error) {
      console.error("Resend OTP failed:", error);
      let errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.error ||
        error?.message;
      throw errorMessage;
    }
  };

  const loginWithGoogle = async (idToken) => {
    try {
      const response = await googleLogin(idToken);
      setUser(response?.user);
      setProfile(response?.profile);
      setToken(response?.token);
      localStorage.setItem("token", response.token);
      toast.success("Login successful!");
      return response?.user;
    } catch (error) {
      console.error("Google login failed:", error);
      let errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.error ||
        error.response?.data?.non_field_errors?.[0] ||
        "Google login failed. Please try again.";
      throw errorMessage;
    }
  };

  const logout = async () => {
    loggingOutRef.current = true;
    setUser(null);
    setProfile(null);
    setToken(null);
    localStorage.removeItem("token");
    navigate("/");

    try {
      await axiosInstance.post("/account/logout/");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  return (
    <AuthContext.Provider
      value={{ user, profile, token, updateProfile, login, loginWithGoogle, logout, register, setUser, setProfile, updateProfilePic, removeProfilePic, verifyOtp: verifyOtpLogin, resendOtp: resendOtpCode, loggingOutRef }}
    >
      {isLoading ? (
        <PageLoader/>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
